import type { LocationHydraItem } from "@verbleif/lib/src/composables/Api/Type/Location/location.interface";
import type { UserHydraItem } from "@verbleif/lib/src/composables/Api/Type/User/user.interface";
import type { MembershipFlagValue } from "@verbleif/lib/src/composables/Api/Type/UserStatus/user_status.enum";
import { PermissionScope } from "@verbleif/lib/src/composables/Api/Type/UserPermissions/userPermission.enum";

import { hasMembershipFlag, UserStateEnum } from "@verbleif/lib/src/composables/Api/Type/UserStatus/user_status.enum";
import { computed, ref } from "vue";
import { useLocationStore } from "../LocationStore/useLocationStore";

export function activeUsersOf(users: Record<string, UserHydraItem>): UserHydraItem[] {
  return Object.values(users).filter(u => u.statuses.some(s => s.state === UserStateEnum.ACTIVE));
}

/** The users that work at `location`: its client managers, its own staff and, on request, its object and group members. */
export function filterUsersForLocation(
  users: UserHydraItem[],
  locations: Record<string, LocationHydraItem>,
  location: string,
  includeOtherUsers: boolean,
): UserHydraItem[] {
  const _client = locations[location].client;

  return users.filter((user) => {
    const managerCheck = user.statuses.some(s => s.client === _client && s.scope === PermissionScope.CLIENT) && (user.statuses.filter(s => s.client === _client).length === 1);

    const receptionCheck = user.statuses.some(s => s.location === location && s.scope !== PermissionScope.USER_GROUP && s.scope !== PermissionScope.PROPERTY);

    const otherUsersCheck = includeOtherUsers ? user.statuses.some(s => s.location === location && (s.scope === PermissionScope.PROPERTY || s.scope === PermissionScope.USER_GROUP)) : false;

    return managerCheck || receptionCheck || otherUsersCheck;
  });
}

export function useSharedStoreUsers() {
  const users = ref<Record<string, UserHydraItem>>({});
  const systemUsers = ref<Record<string, UserHydraItem>>({});
  const { locations } = useLocationStore();

  const allUsers = computed<Record<string, UserHydraItem>>(() => {
    return { ...users.value, ...systemUsers.value };
  });

  const usersList = computed<UserHydraItem[]>(() => activeUsersOf(users.value));

  const systemUsersList = computed<UserHydraItem[]>(() => {
    return Object.values(systemUsers.value);
  });

  const allUsersList = computed<UserHydraItem[]>(() => {
    return [...usersList.value, ...systemUsersList.value];
  });

  function _reset() {
    users.value = {};
    systemUsers.value = {};
  }

  function getActiveUserStatuses(user: UserHydraItem) {
    return user.statuses.filter(status => status.state === UserStateEnum.ACTIVE);
  }

  function getUsersPerUserGroup(membershipFlag?: MembershipFlagValue) {
    return usersList.value.reduce<Record<string, UserHydraItem[]>>((acc, user) => {
      const userGroupStatusses = getActiveUserStatuses(user).filter(
        status => status.userGroup
          && status.scope === PermissionScope.USER_GROUP
          && (membershipFlag === undefined || hasMembershipFlag(status.membershipFlags, membershipFlag)),
      );

      if (!userGroupStatusses.length) {
        return acc;
      }

      userGroupStatusses.forEach((userGroupStatus) => {
        if (!userGroupStatus.userGroup) {
          return;
        }
        if (!acc[userGroupStatus.userGroup]) {
          acc[userGroupStatus.userGroup] = [];
        }
        acc[userGroupStatus.userGroup].push(user);
      });

      return acc;
    }, {} as Record<string, UserHydraItem[]>);
  }

  const usersPerUserGroup = computed<Record<string, UserHydraItem[]>>(() => {
    return getUsersPerUserGroup();
  });

  function getUsersFiltered(location?: string, includeOtherUsers = false) {
    if (location === undefined) {
      return [];
    }

    return filterUsersForLocation(usersList.value, locations.value, location, includeOtherUsers);
  };

  return {
    users,
    usersList,
    systemUsers,
    getUsersPerUserGroup,
    usersPerUserGroup,
    getActiveUserStatuses,
    getUsersFiltered,
    systemUsersList,
    allUsers,
    allUsersList,
    _reset,
  };
}
